import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "react-router-dom"
import { api, toApiMessage } from "../../api/client"
import { ErrorState, LoadingState, PageHeader, showToast } from "../../components/ui"
import { useFamily } from "../../family/FamilyContext"
import { TaskForm, type TaskFormValue } from "./TaskForm"
import type { RecurringTask, TaskOccurrence } from "./types"

function useEditorContext() {
  const { activeFamily } = useFamily()
  const navigate = useNavigate()
  if (!activeFamily) throw new Error("Семья не выбрана")
  return { familyId: activeFamily.id, memberId: activeFamily.memberId, navigate }
}

export function TaskCreatePage() {
  const { familyId, memberId, navigate } = useEditorContext()
  const client = useQueryClient()
  async function submit(value: TaskFormValue) {
    await api.post(`/families/${familyId}/tasks`, {
      ...value,
      recurrence: value.recurrence ?? undefined,
      penalty: value.penalty ?? undefined
    })
    await client.invalidateQueries({ queryKey: ["v1", familyId, "tasks"] })
    showToast("Задача создана")
    navigate("/tasks")
  }
  return (
    <div className="page-stack page-stack--form task-create-page">
      <PageHeader eyebrow="Задачи" title="Новая задача" description="Что сделать → Кому → Когда → Создать" />
      <TaskForm mode="create" currentMemberId={memberId} onSubmit={submit} onCancel={() => navigate("/tasks")} />
    </div>
  )
}

export function TaskEditPage() {
  const { occurrenceId = "" } = useParams()
  const { familyId, memberId, navigate } = useEditorContext()
  const client = useQueryClient()
  const tasks = useQuery({
    queryKey: ["v1", familyId, "tasks", "plan"],
    queryFn: () => api.get<{ items: TaskOccurrence[] }>(`/families/${familyId}/tasks?view=plan`)
  })
  const item = tasks.data?.items.find((candidate) => candidate.id === occurrenceId)

  async function submit(value: TaskFormValue) {
    if (!item) return
    await api.patch(`/families/${familyId}/tasks/definitions/${item.definition.id}`, {
      occurrenceId: item.status === "OPEN" ? item.id : undefined,
      title: value.title,
      description: value.description,
      dueAt: value.dueAt,
      ...(item.status === "OPEN"
        ? { assigneeAudience: value.assigneeAudience, assignmentStrategy: value.assignmentStrategy }
        : {}),
      recurrence: value.recurrence,
      completionMode: value.completionMode,
      approverAudience: value.approverAudience,
      approverMode: value.approverMode,
      overduePolicy: value.overduePolicy,
      visibility: value.visibility,
      reminders: value.reminders,
      skipAdvancesRotation: value.skipAdvancesRotation,
      rewards: value.rewards,
      penalty: value.penalty,
      checklist: value.checklist
    })
    await client.invalidateQueries({ queryKey: ["v1", familyId, "tasks"] })
    showToast("Задача обновлена")
    navigate("/tasks")
  }

  if (tasks.isLoading) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Редактирование" /><LoadingState rows={5} /></div>
  if (tasks.error) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Редактирование" /><ErrorState message={toApiMessage(tasks.error)} retry={() => void tasks.refetch()} /></div>
  if (!item) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Задача не найдена" /><ErrorState title="Не удалось открыть задачу" message="Возможно, задача уже завершена или удалена." /></div>

  return (
    <div className="page-stack page-stack--form task-edit-page">
      <PageHeader eyebrow="Задачи" title="Редактировать задачу" description="Та же форма и порядок, что при создании." />
      <TaskForm
        mode="edit"
        currentMemberId={memberId}
        initial={{ ...item.definition, dueAt: item.dueAt }}
        assigneesLocked={item.status !== "OPEN"}
        onSubmit={submit}
        onCancel={() => navigate("/tasks")}
      />
    </div>
  )
}

export function RecurringTaskEditPage() {
  const { definitionId = "" } = useParams()
  const { familyId, memberId, navigate } = useEditorContext()
  const client = useQueryClient()
  const tasks = useQuery({
    queryKey: ["v1", familyId, "tasks", "recurring"],
    queryFn: () => api.get<{ items: RecurringTask[] }>(`/families/${familyId}/tasks?view=recurring`)
  })
  const task = tasks.data?.items.find((candidate) => candidate.id === definitionId)

  async function submit(value: TaskFormValue) {
    if (!task) return
    await api.patch(`/families/${familyId}/tasks/definitions/${task.id}`, {
      title: value.title,
      description: value.description,
      assigneeAudience: value.assigneeAudience,
      assignmentStrategy: value.assignmentStrategy,
      recurrence: value.recurrence,
      completionMode: value.completionMode,
      approverAudience: value.approverAudience,
      approverMode: value.approverMode,
      overduePolicy: value.overduePolicy,
      visibility: value.visibility,
      reminders: value.reminders,
      skipAdvancesRotation: value.skipAdvancesRotation,
      rewards: value.rewards,
      penalty: value.penalty,
      checklist: value.checklist
    })
    await client.invalidateQueries({ queryKey: ["v1", familyId, "tasks"] })
    showToast("Расписание обновлено")
    navigate("/tasks")
  }

  if (tasks.isLoading) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Редактирование повторения" /><LoadingState rows={5} /></div>
  if (tasks.error) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Редактирование повторения" /><ErrorState message={toApiMessage(tasks.error)} retry={() => void tasks.refetch()} /></div>
  if (!task) return <div className="page-stack page-stack--form"><PageHeader eyebrow="Задачи" title="Повторение не найдено" /><ErrorState title="Не удалось открыть расписание" message="Возможно, оно уже удалено." /></div>

  return (
    <div className="page-stack page-stack--form task-edit-page">
      <PageHeader eyebrow="Задачи" title="Редактировать повторение" description="Настройки задачи и расписания собраны в одной форме." />
      <TaskForm mode="edit" currentMemberId={memberId} initial={task} recurrenceRequired onSubmit={submit} onCancel={() => navigate("/tasks")} />
    </div>
  )
}
